// VicTech #710:把计数(tracker)接到 vuex 事件流、标题、favicon、声音。
// 事件来源 = store.subscribeAction(ActionCable 收到的 conversation.* / message.created 都会 dispatch
// addConversation / updateConversation / addMessage),不改 actionCable.js 本体。
// 另有轮询(预设 60 秒)+ 分页回到前景 + 网路恢复时重抓一次,补漏掉的事件。
//
// VicTech #710-kimi(Kimi 条件②)收件箱过滤:chatchat 一站一收件箱、每站客服只配自己那个收件箱(#709)。
// 伺服器端 ActionCable 本来就只把 conversation.* 推给「该收件箱成员 + 管理员」,这里再加一道:
// 即时事件要「加一」时,对话的 inbox_id 必须在本 agent 可见收件箱清单里(allowedInboxIds);
// 清单还没载入(null)或 payload 缺 inbox_id → 不在即时路径加,交给重抓(伺服器按权限过滤)。
// 不在清单的对话若已在计数里 → 拿掉(例如被移出该收件箱)。未传 allowedInboxIds = 不过滤(单测/旧行为)。
import { NeedHumanTracker, isNeedHuman } from './tracker';

const CONVERSATION_ACTIONS = ['addConversation', 'updateConversation'];
const MESSAGE_ACTIONS = ['addMessage'];

export class NeedHumanController {
  constructor({
    store,
    fetchIds,
    title,
    favicon,
    sound,
    onCount = () => {},
    allowedInboxIds = null,
    tracker = new NeedHumanTracker(),
    pollMs = 60000,
    retryMs = 2000,
    win = typeof window !== 'undefined' ? window : null,
    doc = typeof document !== 'undefined' ? document : null,
  }) {
    Object.assign(this, {
      store,
      fetchIds,
      title,
      favicon,
      sound,
      onCount,
      allowedInboxIds,
      tracker,
      pollMs,
      retryMs,
      win,
      doc,
    });
    this.unsubscribe = null;
    this.pollTimer = null;
    this.retryTimer = null;
    this.eventSeq = 0;
    this.initialised = false;
    this.running = false;
    this.onVisible = () => {
      if (this.doc.visibilityState === 'visible') this.refresh();
    };
    this.onOnline = () => this.refresh();
  }

  start() {
    if (this.running) return Promise.resolve();
    this.running = true;
    this.tracker.reset();
    this.initialised = false;
    this.title?.start();
    this.favicon?.start();
    this.sound?.start();
    this.unsubscribe = this.store.subscribeAction(action =>
      this.onAction(action)
    );
    this.doc?.addEventListener('visibilitychange', this.onVisible);
    this.win?.addEventListener('online', this.onOnline);
    this.pollTimer = setInterval(() => this.refresh(), this.pollMs);
    this.publish();
    return this.refresh();
  }

  stop() {
    this.running = false;
    if (this.unsubscribe) this.unsubscribe();
    this.unsubscribe = null;
    clearInterval(this.pollTimer);
    clearTimeout(this.retryTimer);
    this.doc?.removeEventListener('visibilitychange', this.onVisible);
    this.win?.removeEventListener('online', this.onOnline);
    this.tracker.reset();
    this.title?.stop();
    this.favicon?.stop();
    this.sound?.stop();
    this.onCount(0, false);
  }

  onAction({ type, payload }) {
    if (!this.running || !payload) return;
    let change = null;
    if (CONVERSATION_ACTIONS.includes(type)) {
      // 不完整的 payload(缺 labels/status)不能拿来判断「已移除」,改为重抓
      if (!Array.isArray(payload.labels) || !payload.status) {
        this.scheduleRefresh();
        return;
      }
      const scope = this.inboxScope(payload);
      if (scope === 'unknown') {
        this.scheduleRefresh();
        return;
      }
      change =
        scope === 'outside'
          ? this.tracker.drop(payload.id)
          : this.tracker.onConversation(payload);
    } else if (MESSAGE_ACTIONS.includes(type)) {
      change = this.tracker.onMessage(payload);
    }
    if (!change) return;
    this.eventSeq += 1;
    this.publish();
    if (change === 'added' && this.initialised) this.sound?.play();
  }

  // 'inside' | 'outside' | 'unknown'。只在「会加一」的事件上才需要知道(移除/无关事件一律 inside)。
  inboxScope(payload) {
    if (typeof this.allowedInboxIds !== 'function' || !isNeedHuman(payload))
      return 'inside';
    const allowed = this.allowedInboxIds();
    if (!allowed || payload.inbox_id === undefined || payload.inbox_id === null)
      return 'unknown';
    return allowed.has(String(payload.inbox_id)) ? 'inside' : 'outside';
  }

  scheduleRefresh() {
    clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => this.refresh(), this.retryMs);
  }

  async refresh() {
    if (!this.running) return;
    const seq = this.eventSeq;
    let ids;
    try {
      ids = await this.fetchIds();
    } catch (e) {
      return; // 网路/权限问题:保留现状,下次轮询再试
    }
    if (!this.running) return;
    if (seq !== this.eventSeq) {
      // 抓的期间有即时事件改过计数 → 这份快照可能过期,稍后重抓
      this.scheduleRefresh();
      return;
    }
    const { added } = this.tracker.applySnapshot(ids);
    const wasInitialised = this.initialised;
    this.initialised = true;
    this.publish();
    if (wasInitialised && added.length) this.sound?.play();
  }

  publish() {
    const n = this.tracker.count;
    this.title?.set(n);
    this.favicon?.set(n > 0);
    this.onCount(n, this.initialised);
  }
}
